import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementRange } from 'types/measurement/geckoMeasurementRange'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { getDropdownOptionsForEnum } from 'utils/enum'
import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredIntSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export enum GeckoCustomerTubeInverted {
  TRUE = 'True',
  FALSE = 'False',
}

export const validateTubeMeasurement = (
  tubes: GeckoMeasurement,
  id: string,
  fieldName: string
) => {
  if (!Number.isInteger(tubes.num))
    throw new Error(
      `values measured in tubes should be integers: ${id} ${fieldName} ${tubes.num}`
    )
}

export interface GeckoLegacyBoilerWallJSON {
  type: GeckoFeatureModelType.LegacyBoilerWall
  name: string
  customer_tube_end: number | undefined
  customer_tube_inverted: boolean | undefined
  tube_end: number
  feature_geometry: {} // eslint-disable-line @typescript-eslint/no-empty-object-type
  nominal: {
    wall_thickness: number | undefined
  }
  tmin: number | undefined
}

export const NewGeckoLegacyBoilerWallJSON: GeckoLegacyBoilerWallJSON = {
  type: GeckoFeatureModelType.LegacyBoilerWall,
  name: 'New Wall',
  customer_tube_end: undefined,
  customer_tube_inverted: undefined,
  tube_end: 0,
  feature_geometry: {},
  nominal: {
    wall_thickness: undefined,
  },
  tmin: undefined,
}

export class GeckoLegacyBoilerWall extends GeckoEditableFeature {
  public readonly name: string
  public readonly type: GeckoFeatureModelType.LegacyBoilerWall
  public readonly geometry: GeckoComponentRect
  public readonly customerXEnd: GeckoMeasurement | undefined
  public readonly customerXInverted: boolean
  public readonly wallNominalThickness: GeckoMeasurement | undefined
  public readonly customerGeometry: GeckoComponentRect
  public readonly tmin: GeckoMeasurement | undefined

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    customerXEnd: GeckoMeasurement | undefined,
    customerXInverted: boolean | undefined,
    wallNominal: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined
  ) {
    super(id)
    this.name = name
    this.type = GeckoFeatureModelType.LegacyBoilerWall

    validateTubeMeasurement(
      geometry.start.x,
      id + '  ' + name,
      'GeckoLegacyBoilerWall.geometry.x_start'
    )
    validateTubeMeasurement(
      geometry.end.x,
      id + '  ' + name,
      'GeckoLegacyBoilerWall.geometry.x_end'
    )

    this.geometry = GeckoComponentRect.CreateFromXYs(
      geometry.componentID,
      geometry.start.x.num - 0.5,
      geometry.start.y.num,
      geometry.end.x.num + 0.5,
      geometry.end.y.num,
      geometry.x.unit,
      geometry.y.unit
    )
    this.customerXEnd = customerXEnd
    if (customerXEnd)
      validateTubeMeasurement(
        customerXEnd,
        id,
        'GeckoLegacyBoilerWall.customer_x_end'
      )
    this.customerXInverted = customerXInverted ?? false
    this.wallNominalThickness = wallNominal
    this.customerGeometry = GeckoComponentRect.CreateFromRanges(
      this.geometry.componentID,
      this.convertToCustomerTubeNumbers(this.geometry.x),
      this.geometry.y
    )
    this.tmin = tmin
  }

  public get componentID() {
    return this.geometry.componentID
  }

  public static CreateArrayFromJSON(
    json: unknown,
    componentGeometry: GeckoComponentRect,
    jsonIDs?: Array<string>
  ): ReadonlyArray<GeckoLegacyBoilerWall> | undefined {
    const arr = json as
      | Array<Record<string, string | number | Record<string, unknown>>>
      | undefined
    if (!arr) return undefined

    try {
      const defaultXEnd = componentGeometry.end.x.num

      arr.sort((wallA, wallB) => {
        const tubeEndA = wallA.tube_end as number
        const tubeEndB = wallB.tube_end as number
        const xEndA = parseNumber(tubeEndA) ?? defaultXEnd
        const xEndB = parseNumber(tubeEndB) ?? defaultXEnd
        return xEndA - xEndB
      })

      let x = componentGeometry.start.x.num

      return arr.map((wall, index) => {
        const tubeEnd = wall.tube_end as number
        const newX = parseNumber(tubeEnd) ?? defaultXEnd
        if (x > newX)
          throw Error(
            `Waterwalls not sorted for ${componentGeometry.componentID}`
          )

        const nominalJSON = wall.nominal as Record<string, number>
        const wallNominal = parseNumber(nominalJSON?.wall_thickness)
        const tmin = parseNumber(wall.tmin)

        const waterwallBounds = GeckoComponentRect.CreateFromXYs(
          componentGeometry.componentID,
          x,
          componentGeometry.start.y.num,
          newX,
          componentGeometry.end.y.num,
          componentGeometry.x.unit,
          componentGeometry.y.unit
        )
        const customerXEnd = parseNumber(wall.customer_tube_end)
        const customerXInverted = parseBoolean(wall.customer_tube_inverted)

        const result = new GeckoLegacyBoilerWall(
          jsonIDs ? jsonIDs[index] : String(index),
          parseString(wall.name) ?? '',
          waterwallBounds,
          customerXEnd === undefined
            ? undefined
            : GeckoMeasurement.CreateFromValue(
                customerXEnd,
                GeckoMeasurementUnit.Tubes
              ),
          customerXInverted,
          wallNominal === undefined
            ? undefined
            : weightThicknessValue(
                GeckoMeasurement.CreateFromValue(
                  wallNominal,
                  GeckoMeasurementUnit.Meters
                )
              ),
          tmin === undefined
            ? undefined
            : GeckoMeasurement.CreateFromValue(
                tmin,
                GeckoMeasurementUnit.Meters
              )
        )
        x = newX + 1
        return result
      })
    } catch (e) {
      console.error(e)
      return undefined
    }
  }

  public static CreateMapFromJSON(
    waterwallsJSON: Record<string, GeckoLegacyBoilerWallJSON>,
    componentGeometry: GeckoComponentRect
  ): Map<string, GeckoLegacyBoilerWall> {
    const array = this.CreateArrayFromJSON(
      Object.values(waterwallsJSON),
      componentGeometry,
      Object.keys(waterwallsJSON)
    )
    const result = new Map<string, GeckoLegacyBoilerWall>()
    array?.forEach((wall) => {
      result.set(wall.id, wall)
    })
    return result
  }

  public static CreateFromJSON(
    id: string,
    json: GeckoLegacyBoilerWallJSON
  ): GeckoLegacyBoilerWall {
    const legacyBoilerWall = GeckoLegacyBoilerWall.CreateArrayFromJSON(
      [json],
      GeckoComponentRect.CreateFromXYs(
        id,
        0,
        0,
        10,
        json.tube_end,
        GeckoMeasurementUnit.Tubes,
        GeckoMeasurementUnit.Tubes
      ),
      [id]
    )
    if (!legacyBoilerWall || legacyBoilerWall.length === 0) {
      throw new Error('Failed to create GeckoLegacyBoilerWall from JSON')
    }
    return legacyBoilerWall[0]
  }

  /**
   * A function which converts an x-coordinate (in "Gecko" tube numbers) on the
   * surface of a waterwall to match numbers provided by the customer
   * @param tube     The tube number to convert
   */
  public convertToCustomerTubeNumbers(tube: GeckoMeasurement): GeckoMeasurement

  /**
   * A function which converts an x-coordinate range (in "Gecko" tube numbers) on the
   * surface of a waterwall to return a range to match numbers provided by the customer
   * @param tubes     The tube numbers to convert
   */
  public convertToCustomerTubeNumbers(
    tubes: GeckoMeasurementRange
  ): GeckoMeasurementRange

  public convertToCustomerTubeNumbers(
    tubeOrTubes: GeckoMeasurement | GeckoMeasurementRange
  ): GeckoMeasurement | GeckoMeasurementRange {
    if (!this.customerXEnd && !this.customerXInverted) return tubeOrTubes
    if (tubeOrTubes instanceof GeckoMeasurement) {
      const halfTube = GeckoMeasurement.CreateFromValue(
        0.5,
        GeckoMeasurementUnit.Tubes
      )
      const xEnd = this.geometry.end.x.minus(halfTube)
      const xInverse = xEnd.minus(tubeOrTubes)
      const endValue = this.customerXEnd ?? xEnd
      return this.customerXInverted
        ? endValue.plus(xInverse)
        : endValue.minus(xInverse)
    } else {
      return GeckoMeasurementRange.CreateFromStartEnd(
        this.convertToCustomerTubeNumbers(tubeOrTubes.start),
        this.convertToCustomerTubeNumbers(tubeOrTubes.end)
      )
    }
  }

  public asJSON(): GeckoLegacyBoilerWallJSON {
    return generateLegacyWall(
      this.name,
      this.geometry.end.x,
      this.customerXInverted,
      this.customerXEnd,
      this.wallNominalThickness
    )
  }
}

export function generateLegacyWall(
  name: string,
  tubeEnd: GeckoMeasurement,
  customer_tube_inverted: boolean | undefined,
  customer_tube_end: GeckoMeasurement | undefined,
  wallNominalThickness: GeckoMeasurement | undefined
): GeckoLegacyBoilerWallJSON {
  const JSON: GeckoLegacyBoilerWallJSON = {
    type: GeckoFeatureModelType.LegacyBoilerWall,
    name: name,
    customer_tube_end: customer_tube_end?.num,
    customer_tube_inverted: customer_tube_inverted,
    tube_end: tubeEnd.num - 0.5,
    feature_geometry: {},
    tmin: undefined,
    nominal: {
      wall_thickness:
        wallNominalThickness !== undefined
          ? wallNominalThickness.toNumber(GeckoMeasurementUnit.Meters)
          : undefined,
    },
  }

  return JSON
}

export const LegacyBoilerWallFormFields = (
  featureJSON: GeckoLegacyBoilerWallJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: featureJSON?.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'tube_end',
      label: 'Tube End',
      value: featureJSON?.tube_end ?? 0,
      unit_magnitude: GeckoMeasurementUnit.Tubes,
      validationRule: requiredIntSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'customer_tube_end',
      label: 'Customer Tube End',
      value: featureJSON?.customer_tube_end,
      unit_magnitude: GeckoMeasurementUnit.Tubes,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'customer_tube_inverted',
      label: 'Customer Tube Inverted',
      value: featureJSON?.customer_tube_inverted
        ? GeckoCustomerTubeInverted.TRUE
        : GeckoCustomerTubeInverted.FALSE,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: getDropdownOptionsForEnum(GeckoCustomerTubeInverted),
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    wallThicknessFormField(featureJSON?.nominal?.wall_thickness),
    tminFormField(featureJSON.tmin),
  ]
}

export const LegacyBoilerWallJSONFromFormFields = (
  formFields: FormField[]
): GeckoLegacyBoilerWallJSON => {
  const customerTubeEnd = getFormFieldByDataKey(formFields, 'customer_tube_end')
  return {
    type: GeckoFeatureModelType.LegacyBoilerWall,
    name: getFormFieldByDataKey(formFields, 'name')?.value as string,

    customer_tube_end: customerTubeEnd?.value
      ? parseNumber(customerTubeEnd?.value)
      : undefined,
    customer_tube_inverted: parseBoolean(
      getFormFieldByDataKey(formFields, 'customer_tube_inverted')?.value ===
        GeckoCustomerTubeInverted.TRUE.toString()
    ),

    tube_end: getFormFieldByDataKey(formFields, 'tube_end')?.value as number,
    feature_geometry: {},
    tmin: parseNumber(getFormFieldByDataKey(formFields, 'tmin')?.value),
    nominal: {
      wall_thickness: parseNumber(
        getFormFieldByDataKey(formFields, 'wall_thickness')?.value
      ),
    },
  }
}

export const LegacyBoilerWallFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.LegacyBoilerWall,
  title: 'Legacy Boiler Walls',
  singularName: 'Legacy Boiler Wall',
  fields: ['name', 'tube_end', 'wall_thickness', 'tmin'],
  sortBy: [{ fieldName: 'tube_end', order: 'asc' }],
}
